/**
 * Duration-based pricing (sconto per durata di noleggio).
 *
 * Funzione PURA condivisa FE+BE: dato il totale base di un noleggio e la durata,
 * trova la pricing_rule duration-based applicabile (min_days/max_days + match data)
 * e ricalcola il base. Es. OREA: ≥7 giorni → tariffa settimanale scontata.
 *
 * REGOLA D'ORO MATCH: client (calculatePriceBreakdown) e server (validazione
 * booking) chiamano QUESTA funzione con gli STESSI dati regola (dal boat API) →
 * stesso risultato → niente drift. price_override/percentage NON ri-markupati qui:
 * il boat API passa già l'override markup-ato (come price_per_day).
 */

export interface DurationRule {
  min_days: number;
  max_days: number | null;
  /** 'fixed' (override = €/giorno) | 'percentage' (override = ±%) | 'absolute_adjustment' (override = ±€ sul totale) */
  price_modifier_type: string | null;
  /** Per 'fixed': €/giorno markup-ato. Per 'percentage': es. -18. Per 'absolute': ±€. */
  price_override: number | null;
  priority: number;
  /** Range date concreti `[{start:'YYYY-MM-DD', end:'YYYY-MM-DD'}]`. Vuoto = sempre. */
  date_ranges?: { start: string; end: string }[] | null;
  date_start?: string | null;
  date_end?: string | null;
}

function dateInRanges(dateYmd: string, rule: DurationRule): boolean {
  const ranges: { start: string; end: string }[] = [];
  if (rule.date_start && rule.date_end) ranges.push({ start: rule.date_start.slice(0, 10), end: rule.date_end.slice(0, 10) });
  if (Array.isArray(rule.date_ranges)) {
    for (const r of rule.date_ranges) {
      if (r?.start && r?.end) ranges.push({ start: String(r.start).slice(0, 10), end: String(r.end).slice(0, 10) });
    }
  }
  if (ranges.length === 0) return true; // nessun vincolo data = sempre valida
  return ranges.some((r) => dateYmd >= r.start && dateYmd <= r.end);
}

/**
 * Trova la regola duration applicabile (durata + data check-in), priorità più alta.
 * `totalDays` = fullDays + halfDays (mezza giornata conta 1 ai fini durata).
 */
export function findDurationRule(
  totalDays: number,
  checkInYmd: string,
  rules: DurationRule[] | null | undefined,
): DurationRule | null {
  if (!Array.isArray(rules) || rules.length === 0) return null;
  const matching = rules.filter((r) =>
    totalDays >= (r.min_days ?? 1) &&
    (r.max_days == null || totalDays <= r.max_days) &&
    dateInRanges(checkInYmd, r),
  );
  if (matching.length === 0) return null;
  return matching.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))[0];
}

/**
 * Applica la regola duration al base. Ritorna il nuovo base (arrotondato a 2 dec)
 * + la regola applicata (null se nessuna).
 */
export function applyDurationPricing(opts: {
  baseTotal: number;
  fullDays: number;
  halfDays: number;
  /** Totale dei soli giorni interi (per ricalcolo per-giorno con override). */
  fullDayTotal: number;
  /** Totale delle sole mezze giornate (resta invariato con override per-giorno). */
  halfDayTotal: number;
  checkInYmd: string;
  rules: DurationRule[] | null | undefined;
}): { adjustedBase: number; appliedRule: DurationRule | null } {
  const totalDays = opts.fullDays + opts.halfDays;
  const rule = findDurationRule(totalDays, opts.checkInYmd, opts.rules);
  if (!rule) return { adjustedBase: opts.baseTotal, appliedRule: null };

  const ov = rule.price_override != null ? Number(rule.price_override) : NaN;
  let adjusted = opts.baseTotal;
  switch (rule.price_modifier_type) {
    case 'percentage':
      if (!isNaN(ov)) adjusted = opts.baseTotal * (1 + ov / 100);
      break;
    case 'absolute_adjustment':
      if (!isNaN(ov)) adjusted = opts.baseTotal + ov;
      break;
    case 'fixed':
    default:
      // override = €/giorno (settimanale). Ricalcola i giorni interi all'override,
      // mantiene le mezze giornate al loro prezzo.
      if (!isNaN(ov)) adjusted = ov * opts.fullDays + opts.halfDayTotal;
      break;
  }
  return { adjustedBase: Math.round(adjusted * 100) / 100, appliedRule: rule };
}
